import { Injectable, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseSignalRService } from './base-signalR.service';

export interface ConnectionRequest {
    userId: string;
    priority: 'high' | 'normal' | 'low';
    timestamp: number;
}

/**
 * Service to manage SignalR connections with connection pooling and rate limiting
 */
@Injectable({
    providedIn: 'root'
})
export class SignalRConnectionManagerService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly baseSignalRService = inject(BaseSignalRService);

    // Connection management
    private connectionQueue: ConnectionRequest[] = [];
    private isProcessingQueue = false;
    private maxConcurrentConnections = 1; // Limit to 1 connection at a time
    private activeConnections = 0;
    private connectionCooldown = 2000; // 2 seconds cooldown between connections
    private lastConnectionTime = 0;

    // State tracking
    private connectionStatus$ = new BehaviorSubject<'idle' | 'connecting' | 'connected' | 'error'>('idle');
    private queueLength$ = new BehaviorSubject<number>(0);
    private errorMessage$ = new Subject<string>();

    // Exposed observables
    readonly connectionStatus = this.connectionStatus$.asObservable();
    readonly queueLength = this.queueLength$.asObservable();
    readonly errorMessage = this.errorMessage$.asObservable();

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.clearQueue();
        });
    }

    /**
     * Request a SignalR connection with priority queuing
     */
    async requestConnection(userId: string, priority: 'high' | 'normal' | 'low' = 'normal'): Promise<void> {
        const request: ConnectionRequest = {
            userId,
            priority,
            timestamp: Date.now()
        };

        // Add to queue based on priority
        this.addToQueue(request);
        this.queueLength$.next(this.connectionQueue.length);

        // Start processing if not already processing
        if (!this.isProcessingQueue) {
            this.processQueue();
        }
    }

    /**
     * Add request to queue with priority ordering
     */
    private addToQueue(request: ConnectionRequest): void {
        const priorityOrder = { high: 3, normal: 2, low: 1 };

        // Find insertion point based on priority
        let insertIndex = this.connectionQueue.length;
        for (let i = 0; i < this.connectionQueue.length; i++) {
            const currentPriority = priorityOrder[this.connectionQueue[i].priority];
            const newPriority = priorityOrder[request.priority];

            if (newPriority > currentPriority) {
                insertIndex = i;
                break;
            }
        }

        this.connectionQueue.splice(insertIndex, 0, request);
        console.log(`Added connection request for user ${request.userId} with ${request.priority} priority. Queue length: ${this.connectionQueue.length}`);
    }

    /**
     * Process the connection queue
     */
    private async processQueue(): Promise<void> {
        if (this.isProcessingQueue || this.connectionQueue.length === 0) {
            return;
        }

        this.isProcessingQueue = true;

        while (this.connectionQueue.length > 0 && this.activeConnections < this.maxConcurrentConnections) {
            const request = this.connectionQueue.shift()!;
            this.queueLength$.next(this.connectionQueue.length);

            // Check cooldown period
            const timeSinceLastConnection = Date.now() - this.lastConnectionTime;
            if (timeSinceLastConnection < this.connectionCooldown) {
                const waitTime = this.connectionCooldown - timeSinceLastConnection;
                console.log(`Waiting ${waitTime}ms before next connection attempt...`);
                await new Promise(resolve => setTimeout(resolve, waitTime));
            }

            try {
                await this.establishConnection(request);
            } catch (error) {
                console.error(`Failed to establish connection for user ${request.userId}:`, error);
                this.errorMessage$.next(`Connection failed for user ${request.userId}: ${error}`);

                // Re-queue with lower priority if it's a rate limit error
                if (error instanceof Error &&
                    (error.message.includes('429') || error.message.includes('Connection count reaches limit'))) {
                    console.log('Rate limit hit, re-queuing with lower priority...');
                    this.addToQueue({
                        ...request,
                        priority: 'low',
                        timestamp: Date.now()
                    });
                    this.queueLength$.next(this.connectionQueue.length);

                    // Wait longer before next attempt
                    await new Promise(resolve => setTimeout(resolve, 10000));
                }
            }
        }

        this.isProcessingQueue = false;

        // If there are still items in queue, schedule next processing
        if (this.connectionQueue.length > 0) {
            setTimeout(() => this.processQueue(), 1000);
        }
    }

    /**
     * Establish connection for a specific request
     */
    private async establishConnection(request: ConnectionRequest): Promise<void> {
        this.connectionStatus$.next('connecting');
        this.activeConnections++;
        this.lastConnectionTime = Date.now();

        try {
            console.log(`Establishing connection for user ${request.userId} (priority: ${request.priority})`);
            await this.baseSignalRService.connect(request.userId);
            this.connectionStatus$.next('connected');
            console.log(`Successfully connected user ${request.userId}`);
        } catch (error) {
            this.connectionStatus$.next('error');
            throw error;
        } finally {
            this.activeConnections--;
        }
    }

    /**
     * Clear the connection queue
     */
    clearQueue(): void {
        this.connectionQueue = [];
        this.queueLength$.next(0);
        this.isProcessingQueue = false;
        console.log('Connection queue cleared');
    }

    /**
     * Get current queue status
     */
    getQueueStatus(): { length: number; isProcessing: boolean; activeConnections: number } {
        return {
            length: this.connectionQueue.length,
            isProcessing: this.isProcessingQueue,
            activeConnections: this.activeConnections
        };
    }

    /**
     * Cancel connection request for a specific user
     */
    cancelConnection(userId: string): boolean {
        const initialLength = this.connectionQueue.length;
        this.connectionQueue = this.connectionQueue.filter(request => request.userId !== userId);
        const removed = initialLength !== this.connectionQueue.length;

        if (removed) {
            this.queueLength$.next(this.connectionQueue.length);
            console.log(`Cancelled connection request for user ${userId}`);
        }

        return removed;
    }

    /**
     * Get connection health status
     */
    isConnectionHealthy(): boolean {
        return this.baseSignalRService.isConnectionHealthy();
    }

    /**
     * Get current connection state
     */
    getConnectionState() {
        return this.baseSignalRService.getConnectionState();
    }

    /**
     * Disconnect current connection
     */
    async disconnect(): Promise<void> {
        await this.baseSignalRService.disconnect();
        this.connectionStatus$.next('idle');
        this.clearQueue();
    }

    /**
     * Listen for messages using the base service
     */
    listenFor<T = any>(messageType: string): Observable<T> {
        return this.baseSignalRService.listenFor<T>(messageType);
    }

    /**
     * Send message using the base service
     */
    async sendMessage(message: string): Promise<void> {
        await this.baseSignalRService.sendMessage(message);
    }

    /**
     * Send private message using the base service
     */
    async sendPrivateMessage(userId: string, message: string): Promise<void> {
        await this.baseSignalRService.sendPrivateMessage(userId, message);
    }
} 